import { onScopeDispose, reactive, toRefs, watch, type ToRefs } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import type { LocationQuery } from 'vue-router'
import { todayIsoDate } from '@/utils/dateUtils'
import { firstQueryValue } from '@/utils/routeQuery'
import {
  saveAgendaViewPreference,
  sessionAgendaViewPreference,
} from '@/composables/useAgendaViewPreference'

/**
 * Shared, library-agnostic serialization of agenda view-state to/from the URL
 * query string.
 *
 * Navigating away from the agenda (e.g. into an appointment detail) creates a
 * browser history entry that carries the current query string, so the back
 * button restores the prior agenda state instead of resetting to today /
 * default view / personal mode.
 *
 * The `view` granularity is intentionally library-agnostic (`day | week |
 * workweek | month`) so that the URL does not carry FullCalendar's own view
 * names.
 */

/** Library-agnostic calendar granularities. */
export const AGENDA_CALENDAR_VIEWS = ['day', 'week', 'workweek', 'month'] as const
export type AgendaCalendarView = (typeof AGENDA_CALENDAR_VIEWS)[number]

/**
 * Default granularity for the combined (resource) agenda. The combined calendar
 * only offers day/week, so it opens on `day` — and falls back to it when the URL
 * carries a granularity it cannot render (`workweek`/`month`, left over from the
 * personal view).
 *
 * This used to have to match the default of the AgendaView-owned URL-state
 * instance, because that instance re-serialised the whole state on every filter
 * change and so stripped any `?view` equal to *its* default (#3161). Instances
 * now write only the fields they change (#3188), so the constant is once again
 * just the combined calendar's own default.
 */
export const COMBINED_AGENDA_DEFAULT_VIEW: AgendaCalendarView = 'day'

/** Agenda display modes (personal calendar vs. combined resource view). */
export const AGENDA_VIEW_MODES = ['personal', 'combined'] as const
export type AgendaViewMode = (typeof AGENDA_VIEW_MODES)[number]

/**
 * Fully-resolved agenda view-state (no missing/invalid values).
 *
 * Every field name doubles as its query-parameter name.
 */
export interface AgendaUrlState {
  /** Selected/visible date as an ISO calendar date string (YYYY-MM-DD). */
  date: string
  /** Calendar granularity. */
  view: AgendaCalendarView
  /** Personal or combined view. */
  mode: AgendaViewMode
  /** Whether the "Aanwezig" follow-the-day lens is active (combined view). */
  aanwezig: boolean
  /**
   * Whether the combined view's people picker also lists the administrative
   * staff on the agenda roster, on top of the designated behandelaren (#3596).
   */
  iedereen: boolean
  /** Selected colleague IDs (combined view). */
  colleagues: string[]
  /** Selected location ID (combined view), or null when none. */
  location: string | null
  /** Selected room IDs (combined view). */
  rooms: string[]
}

export interface UseAgendaUrlStateOptions {
  /** Override built-in defaults used when a param is absent or invalid. */
  defaults?: Partial<AgendaUrlState>
  /** Debounce window for URL writes, in milliseconds. */
  debounceMs?: number
}

export interface UseAgendaUrlStateReturn extends ToRefs<AgendaUrlState> {
  /** Patch one or more fields at once. */
  setState: (patch: Partial<AgendaUrlState>) => void
  /** Reset all fields back to their defaults. */
  reset: () => void
  /** The resolved defaults in effect for this instance. */
  defaults: Readonly<AgendaUrlState>
}

const DEFAULT_VIEW: AgendaCalendarView = 'week'
const DEFAULT_DEBOUNCE_MS = 300
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Every state field, in query-parameter order. Each name doubles as its
 * query-parameter name, so this is also the definitive list of "agenda params"
 * for anything outside this composable that has to recognise them — see the
 * view-state restore guard in `router/agenda-view-state.ts`.
 */
export const AGENDA_STATE_FIELDS = [
  'date',
  'view',
  'mode',
  'aanwezig',
  'iedereen',
  'colleagues',
  'location',
  'rooms',
] as const
type AgendaStateField = (typeof AGENDA_STATE_FIELDS)[number]

/** True when `value` is a real calendar date in YYYY-MM-DD form. */
function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) {
    return false
  }
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7))
  const day = Number(value.slice(8, 10))
  const date = new Date(year, month - 1, day)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

function parseDate(value: string | undefined, fallback: string): string {
  return value !== undefined && isValidIsoDate(value) ? value : fallback
}

function parseEnum<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
  fallback: T,
): T {
  return value !== undefined && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

/** Parse a comma-separated list, trimming blanks and de-duplicating. */
function parseList(value: string | undefined): string[] {
  if (!value) {
    return []
  }
  const seen = new Set<string>()
  for (const part of value.split(',')) {
    const trimmed = part.trim()
    if (trimmed) {
      seen.add(trimmed)
    }
  }
  return [...seen]
}

function parseLocation(value: string | undefined, fallback: string | null): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

/** Parse a boolean flag param (`1`/`0`); anything else resolves to the fallback. */
function parseFlag(value: string | undefined, fallback: boolean): boolean {
  if (value === '1') return true
  if (value === '0') return false
  return fallback
}

function setParam(query: LocationQuery, key: string, value: string | undefined): void {
  if (value === undefined) {
    delete query[key]
  } else {
    query[key] = value
  }
}

/** Shallow value-equality for two query objects (treats arrays as joined). */
function queryEquals(a: LocationQuery, b: LocationQuery): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) {
    return false
  }
  return aKeys.every((key) => String(a[key]) === String(b[key]))
}

type AgendaStateValue = AgendaUrlState[AgendaStateField]

/** Value-equality for a single field (list fields compare element-wise). */
function fieldEquals(a: AgendaStateValue, b: AgendaStateValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((id, i) => id === b[i])
  }
  return a === b
}

/** The fields on which `to` differs from `from`. */
function driftedFields(from: AgendaUrlState, to: AgendaUrlState): AgendaStateField[] {
  return AGENDA_STATE_FIELDS.filter((field) => !fieldEquals(from[field], to[field]))
}

/** Copy a single field across, keeping the per-field types correlated. */
function copyField<K extends AgendaStateField>(
  target: AgendaUrlState,
  source: AgendaUrlState,
  field: K,
): void {
  target[field] = source[field]
}

/** Detach a plain copy from a (possibly reactive) state object. */
function snapshot(state: AgendaUrlState): AgendaUrlState {
  return { ...state, colleagues: [...state.colleagues], rooms: [...state.rooms] }
}

export function useAgendaUrlState(
  options: UseAgendaUrlStateOptions = {},
): UseAgendaUrlStateReturn {
  const route = useRoute()
  const router = useRouter()
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS

  // The last-used agenda mode, Aanwezig lens and roster breadth are durable
  // preferences (#3319, #3596): they seed the defaults so a param-less fresh
  // arrival opens the agenda the user lives in, while an explicit URL param
  // still wins. Read via the per-page-load snapshot so every instance resolves
  // an absent param the same way (see `sessionAgendaViewPreference`).
  const preference = sessionAgendaViewPreference()

  const defaults: AgendaUrlState = {
    date: todayIsoDate(),
    view: DEFAULT_VIEW,
    mode: preference.mode,
    aanwezig: preference.aanwezig,
    iedereen: preference.iedereen,
    colleagues: [],
    location: null,
    rooms: [],
    ...options.defaults,
  }

  /** Derive a fully-resolved state from the current URL query. */
  function readState(): AgendaUrlState {
    const q = route.query
    return {
      date: parseDate(firstQueryValue(q.date), defaults.date),
      view: parseEnum(firstQueryValue(q.view), AGENDA_CALENDAR_VIEWS, defaults.view),
      mode: parseEnum(firstQueryValue(q.mode), AGENDA_VIEW_MODES, defaults.mode),
      aanwezig: parseFlag(firstQueryValue(q.aanwezig), defaults.aanwezig),
      iedereen: parseFlag(firstQueryValue(q.iedereen), defaults.iedereen),
      colleagues: parseList(firstQueryValue(q.colleagues)),
      location: parseLocation(firstQueryValue(q.location), defaults.location),
      rooms: parseList(firstQueryValue(q.rooms)),
    }
  }

  const state = reactive<AgendaUrlState>(readState())

  /**
   * The state exactly as this instance last saw it in the URL — the reference
   * both directions diff against, so an instance writes only the fields it
   * changed itself and adopts only the fields the URL changed under it.
   *
   * The agenda keeps several instances alive at once: the view drives `?mode`
   * and the combined-view filters, the mounted calendar drives `?date`/`?view`,
   * and each holds a full copy of the state. Exchanging whole copies made every
   * write re-assert fields the instance had merely echoed, and every incoming
   * write reset fields the instance had just changed — so the last writer won
   * on every field, and two instances whose debounce windows overlapped could
   * clobber each other (#3188).
   */
  let urlSnapshot = snapshot(state)

  /** Serialize one field, or `undefined` when the param should be omitted. */
  function toParam(field: AgendaStateField, s: AgendaUrlState): string | undefined {
    switch (field) {
      case 'date':
        return s.date === defaults.date ? undefined : s.date
      case 'view':
        return s.view === defaults.view ? undefined : s.view
      case 'mode':
        return s.mode === defaults.mode ? undefined : s.mode
      case 'aanwezig':
        return s.aanwezig === defaults.aanwezig ? undefined : s.aanwezig ? '1' : '0'
      case 'iedereen':
        return s.iedereen === defaults.iedereen ? undefined : s.iedereen ? '1' : '0'
      case 'colleagues':
        return s.colleagues.length ? s.colleagues.join(',') : undefined
      case 'location':
        return s.location ?? undefined
      case 'rooms':
        return s.rooms.length ? s.rooms.join(',') : undefined
    }
  }

  /**
   * Lay the fields that drifted from {@link urlSnapshot} on top of the current
   * query, leaving every other param — ours or another instance's — untouched.
   * A field back at its default is written as a removal, since absence resolves
   * to the default on read.
   */
  function toQuery(s: AgendaUrlState): LocationQuery {
    const next: LocationQuery = { ...route.query }
    for (const field of driftedFields(urlSnapshot, s)) {
      setParam(next, field, toParam(field, s))
    }
    return next
  }

  /**
   * A scheduled write outlives the scope that scheduled it, and it carries the
   * *whole* state of its instance — `mode` included. Switching agenda mode
   * unmounts one calendar and mounts the other, so a write still queued for the
   * calendar the user just left lands afterwards and puts its own `mode` back in
   * the URL, whose echo flips the agenda straight back to that view (#3185).
   * Anything still queued at dispose belongs to a view that is gone; drop it.
   */
  let disposed = false
  onScopeDispose(() => {
    disposed = true
  })

  // Debounced because FullCalendar's `datesSet` fires on every navigation step;
  // without debounce + replace the history floods and back-nav becomes useless.
  const writeToUrl = useDebounceFn(() => {
    if (disposed) {
      return
    }
    const next = toQuery(state)
    urlSnapshot = snapshot(state)
    if (!queryEquals(next, route.query)) {
      void router.replace({ query: next })
    }
  }, debounceMs)

  // Local state is the source of truth for the UI; mirror changes to the URL.
  watch(state, () => writeToUrl())

  // Mirror external URL changes (browser back/forward, or a sibling instance's
  // write) back into state — but only the fields the URL actually changed, so a
  // sibling's write can't roll back a change of ours that is still debounced.
  watch(
    () => route.query,
    () => {
      const incoming = readState()
      for (const field of driftedFields(urlSnapshot, incoming)) {
        copyField(state, incoming, field)
      }
      urlSnapshot = snapshot(incoming)
    },
  )

  // Persist the durable preferences (#3319). Every change counts, whether it
  // came from the UI or was adopted from the URL — a deep link the user opened
  // is as much "where they last were" as a click. Multiple instances saving the
  // same values is idempotent.
  watch(
    () => [state.mode, state.aanwezig, state.iedereen] as const,
    ([mode, aanwezig, iedereen]) => saveAgendaViewPreference({ mode, aanwezig, iedereen }),
  )

  function setState(patch: Partial<AgendaUrlState>): void {
    Object.assign(state, patch)
  }

  function reset(): void {
    Object.assign(state, {
      ...defaults,
      colleagues: [...defaults.colleagues],
      rooms: [...defaults.rooms],
    })
  }

  return {
    ...toRefs(state),
    setState,
    reset,
    defaults,
  }
}
